import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import type { ChatbotConversationType } from '@/types/ChatbotConversationType';
import type { ChatbotMessageType, ToolCallType } from '@/types/ChatbotMessageType';
import type {
    ChatbotStateType, ChatbotGettersType, ChatbotActionsType,
} from '@/types/stores/ChatbotStoreTypes';
import type {
    StreamingBufferType,
    StreamingToolCallType,
    StreamingMessageMetadata,
    SimulationProposalType,
} from '@/types/StreamingTypes';
import type {
    AiChatType,
    AiChatApiMessageType,
    AiChatListResponseType,
    AiChatDetailResponseType,
} from '@/types/api/AiChatApiTypes';
import { handleRequest, useStreamingChat } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import { ChatStreamError } from '@/types/errors/ChatStreamError';
import i18n from '@/plugins/i18n';

const mapApiChatToConversation = (apiChat: AiChatType): ChatbotConversationType => ({
    id: apiChat.id,
    title: apiChat.name || i18n.global.t('chatbot.newConversation'),
    messages: [],
    createdAt: apiChat.created_at,
    updatedAt: apiChat.updated_at,
    isWelcome: apiChat.is_welcome,
});

const mapApiMessagesToMessages = (apiMessages: AiChatApiMessageType[]): ChatbotMessageType[] => {
    const result: ChatbotMessageType[] = [];
    for (const [index, msg] of apiMessages.entries()) {
        result.push({
            id: `msg-${index}`,
            text: msg.content,
            sender: msg.role === 'user' ? 'user' : 'bot',
            timestamp: new Date().toISOString(),
            type: 'text',
            status: 'completed',
        });
    }

    return result;
};

export const useChatbotStore = defineStore<'chatbot', ChatbotStateType, ChatbotGettersType, ChatbotActionsType>(
    'chatbot',
    {
        state: () => ({
            isOpen: false,
            isTyping: false,
            currentConversation: null,
            conversations: [],
            currentMessage: '',
            searchQuery: '',
            sidebarExpanded: true,
            isLoadingConversations: false,
            isLoadingChat: false,
            isDraftMode: false,
            isDeletingConversationId: null as number | string | null,
        }),
        getters: {
            hasActiveConversation: (state: ChatbotStateType): boolean => !!state.currentConversation,
            currentMessages: (state: ChatbotStateType): ChatbotMessageType[] =>
                state.currentConversation?.messages || [],
            conversationCount: (state: ChatbotStateType): number => state.conversations.length,
            sortedConversations: (state: ChatbotStateType): ChatbotConversationType[] => {
                return [...state.conversations].sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                );
            },
            searchResults: (state: ChatbotStateType): ChatbotConversationType[] => {
                if (!state.searchQuery.trim()) {
                    return [...state.conversations].sort(
                        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                    );
                }

                const query = state.searchQuery.toLowerCase();
                const sortedConversations = [...state.conversations].sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                );

                return sortedConversations.filter(
                    (conv: ChatbotConversationType) =>
                        conv.title.toLowerCase().includes(query) ||
                        conv.messages.some((msg: ChatbotMessageType) => msg.text.toLowerCase().includes(query)),
                );
            },
        },

        actions: {
            async toggleChatbot(): Promise<void> {
                if (!this.isOpen && !this.currentConversation) {
                    await this.startNewConversation();
                }
                this.isOpen = !this.isOpen;
            },

            async sendMessage(message: string): Promise<void> {
                if (!message.trim()) {
                    return;
                }

                // V draft mode nejprve vytvoříme chat
                if (!this.currentConversation) {
                    const chatName = message.trim().slice(0, 25);
                    const newChat = await this.createChat(chatName);
                    if (!newChat) {
                        return;
                    }
                }

                const userMessage: ChatbotMessageType = {
                    id: uuidv4(),
                    text: message.trim(),
                    sender: 'user',
                    timestamp: new Date().toISOString(),
                    type: 'text',
                    status: 'completed',
                };

                this.addMessage(userMessage);
                this.currentMessage = '';

                // Don't set global typing when we have streaming message
                // this.setTyping(true);

                const botMessageId = uuidv4();
                const botMessage: ChatbotMessageType = {
                    id: botMessageId,
                    text: '',
                    sender: 'bot',
                    timestamp: new Date().toISOString(),
                    type: 'text',
                    status: 'streaming',
                    isStreaming: true,
                    streamingBuffer: '',
                    toolCalls: [],
                    reasoning: '',
                    isReasoningCollapsed: undefined,
                    hasReceivedMessage: false,
                    hasReceivedReasoning: false,
                };

                this.addMessage(botMessage);

                try {
                    await this.sendStreamingMessage(message, botMessageId);

                    // this.setTyping(false); // Not needed since we didn't set it
                } catch (error) {
                    console.error('Error getting bot response:', error);
                    this.updateMessageStatus(botMessageId, 'error');

                    // this.setTyping(false); // Not needed since we didn't set it
                }
            },

            async createChat(name: string): Promise<ChatbotConversationType | null> {
                let newConversation: ChatbotConversationType | null = null;
                this.isLoadingChat = true;

                await handleRequest({
                    method: RequestMethodEnum.Post,
                    endpoint: '/ai-chats',
                    data: { name },
                    onSuccess: (response: { data: { data: AiChatType } }) => {
                        newConversation = mapApiChatToConversation(response.data.data);
                        this.conversations.unshift(newConversation);
                        this.currentConversation = newConversation;
                        this.isDraftMode = false;
                    },
                });

                this.isLoadingChat = false;

                return newConversation;
            },

            startDraftMode(): void {
                this.currentConversation = null;
                this.isDraftMode = true;
                this.currentMessage = '';
            },

            async startNewConversation(): Promise<void> {
                this.startDraftMode();
            },
            async loadConversation(conversationId: number | string): Promise<void> {
                this.isLoadingChat = true;
                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint: `/ai-chats/${conversationId}`,
                    onSuccess: (response: { data: AiChatDetailResponseType }) => {
                        const responseData = response.data;
                        const conversation = mapApiChatToConversation(responseData.data);
                        conversation.messages = mapApiMessagesToMessages(responseData.messages);

                        // Update in conversations array (normalize ID type – route params are strings, API returns numbers)
                        const index = this.conversations.findIndex(c => Number(c.id) === Number(conversationId));
                        if (index > -1) {
                            this.conversations[index] = conversation;
                        }
                        this.currentConversation = conversation;
                    },
                    canShowSnackbar: false,
                });
                this.isLoadingChat = false;
            },

            async deleteConversation(conversationId: number | string): Promise<void> {
                this.isDeletingConversationId = conversationId;
                await handleRequest({
                    method: RequestMethodEnum.Delete,
                    endpoint: `/ai-chats/${conversationId}`,
                    onSuccess: () => {
                        const index = this.conversations.findIndex(c => c.id === conversationId);
                        if (index > -1) {
                            this.conversations.splice(index, 1);
                        }
                        if (this.currentConversation?.id === conversationId) {
                            // Po smazání aktuálního chatu přejít do draft mode
                            this.currentConversation = null;
                            this.isDraftMode = true;
                        }
                    },
                });
                this.isDeletingConversationId = null;
            },

            setSearchQuery(query: string): void {
                this.searchQuery = query;
            },
            setTyping(isTyping: boolean): void {
                this.isTyping = isTyping;
            },
            updateCurrentMessage(message: string): void {
                this.currentMessage = message;
            },

            addMessage(message: ChatbotMessageType): void {
                if (this.currentConversation) {
                    this.currentConversation.messages.push(message);
                    this.currentConversation.updatedAt = new Date().toISOString();
                }
            },

            async getBotResponse(userMessage: string): Promise<string> {
                const response = await handleRequest({
                    method: RequestMethodEnum.Post,
                    endpoint: '/ai-chat/message',
                    data: { message: userMessage },
                    canShowSnackbar: false,
                });

                return response.data.text;
            },

            async sendStreamingMessage(userMessage: string, messageId: string): Promise<void> {
                const { streamMessage } = useStreamingChat();
                const chatId = this.currentConversation?.id;

                if (chatId === undefined) {
                    console.error('[sendStreamingMessage] No active conversation');
                    throw new Error('No active conversation');
                }

                try {
                    await streamMessage(userMessage, messageId, Number(chatId), {
                        onTextUpdate: text => this.updateProgressiveText(messageId, text),
                        onReasoningUpdate: reasoning => this.updateReasoningText(messageId, reasoning),
                        onToolCall: toolCall => this.addToolCallToMessage(messageId, toolCall),
                        onMetadata: metadata => this.updateMessageMetadata(messageId, metadata),
                        onComplete: buffer => this.finalizeStreamingMessage(messageId, buffer),
                        onError: error => {
                            console.error('Streaming error:', error);
                            this.handleStreamError(messageId, error);
                        },
                    });
                } catch (error) {
                    console.error('Error in streaming message:', error);
                    this.addErrorMessageToChat(error, messageId);
                    throw error;
                }
            },

            findMessageInConversations(messageId: string): ChatbotMessageType | undefined {
                for (const conv of this.conversations) {
                    const msg = conv.messages.find(m => m.id === messageId);
                    if (msg) {
                        return msg;
                    }
                }

                return undefined;
            },

            updateProgressiveText(messageId: string, text: string): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                message.streamingBuffer = text;
                message.text = text;
                message.hasReceivedMessage = true;

                // Auto-collapse reasoning if it was pulsing
                if (message.hasReceivedReasoning && message.isReasoningCollapsed === undefined) {
                    message.isReasoningCollapsed = true;
                }
            },

            updateMessageText(messageId: string, text: string): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                message.text = text;
                message.streamingBuffer = text;
            },

            updateMessageStatus(messageId: string, status: 'pending' | 'streaming' | 'completed' | 'error'): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                message.status = status;
                if (status === 'completed' || status === 'error') {
                    message.isStreaming = false;
                }
            },

            updateMessageMetadata(messageId: string, metadata: StreamingMessageMetadata): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                message.messageId = metadata.messageId;
            },

            addToolCallToMessage(messageId: string, toolCall: StreamingToolCallType): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                if (!message.toolCalls) {
                    message.toolCalls = [];
                }

                const toolCallForMessage: ToolCallType = {
                    id: toolCall.toolCallId,
                    toolName: toolCall.toolName,
                    status: 'executing',
                    metadata: toolCall.arguments,
                };

                message.toolCalls.push(toolCallForMessage);

                if (message.toolCalls.length > 0) {
                    message.type = 'tool-call';
                }
            },

            finalizeStreamingMessage(messageId: string, buffer: StreamingBufferType): void {
                let targetConversation: ChatbotConversationType | undefined;
                let message: ChatbotMessageType | undefined;

                for (const conv of this.conversations) {
                    const msg = conv.messages.find(m => m.id === messageId);
                    if (msg) {
                        targetConversation = conv;
                        message = msg;
                        break;
                    }
                }

                if (!message) {
                    return;
                }

                message.text = buffer.textBuffer;
                message.reasoning = buffer.reasoningBuffer;
                message.status = 'completed';
                message.isStreaming = false;
                message.streamingBuffer = '';
                message.simulationProposals =
                    buffer.simulationProposals.length > 0 ? buffer.simulationProposals : undefined;

                if (message.hasReceivedReasoning) {
                    message.isReasoningCollapsed = true;
                }

                if (message.toolCalls) {
                    message.toolCalls = [];
                }

                if (
                    targetConversation &&
                    buffer.conversationTitle &&
                    targetConversation.title !== buffer.conversationTitle
                ) {
                    targetConversation.title = buffer.conversationTitle;
                }
            },

            updateReasoningText(messageId: string, reasoning: string): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }
                message.reasoning = reasoning;
                message.hasReceivedReasoning = true;

                // Auto-collapse if message content already started flowing
                if (message.hasReceivedMessage && message.isReasoningCollapsed === undefined) {
                    message.isReasoningCollapsed = true;
                }
            },

            toggleReasoningCollapsed(messageId: string): void {
                const message = this.findMessageInConversations(messageId);
                if (message && message.isReasoningCollapsed !== undefined) {
                    message.isReasoningCollapsed = !message.isReasoningCollapsed;
                }
            },

            handleStreamError(messageId: string, error: unknown): void {
                const message = this.findMessageInConversations(messageId);
                if (!message) {
                    return;
                }

                if (error instanceof ChatStreamError) {
                    message.text = error.message;
                    message.streamingBuffer = '';
                    message.isStreaming = false;
                    message.status = 'error';
                    message.type = 'text';

                    return;
                }

                this.updateMessageStatus(messageId, 'error');
            },

            addErrorMessageToChat(error: unknown, failedMessageId?: string): void {
                let targetConversation: ChatbotConversationType | null = this.currentConversation;

                // Remove the failed empty message and find its conversation
                if (failedMessageId) {
                    for (const conv of this.conversations) {
                        const idx = conv.messages.findIndex(m => m.id === failedMessageId);
                        if (idx !== -1) {
                            conv.messages.splice(idx, 1);
                            targetConversation = conv;
                            break;
                        }
                    }
                }

                if (!targetConversation) {
                    return;
                }

                let errorMessage = i18n.global.t('chatbot.errorSendingMessage');

                // Extract meaningful error messages from different error types
                if (error instanceof ChatStreamError) {
                    // Server already rendered a user-facing detail (e.g. 429 daily limit) — show it as-is.
                    errorMessage = error.message;
                } else if (error instanceof Error) {
                    if (error.message.includes('Streaming request failed:')) {
                        // API returned HTTP error
                        const statusMatch = error.message.match(/Streaming request failed: (.+)/);
                        if (statusMatch) {
                            errorMessage = i18n.global.t('chatbot.apiError', { status: statusMatch[1] });
                        }
                    } else if (error.message.includes('Unable to read streaming response')) {
                        errorMessage = i18n.global.t('chatbot.streamingError');
                    } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
                        errorMessage = i18n.global.t('chatbot.networkError');
                    } else {
                        // Generic error with message
                        errorMessage = `${i18n.global.t('chatbot.errorSendingMessage')}: ${error.message}`;
                    }
                }

                const errorBotMessage: ChatbotMessageType = {
                    id: uuidv4(),
                    text: errorMessage,
                    sender: 'bot',
                    timestamp: new Date().toISOString(),
                    type: 'text',
                    status: 'error',
                };

                targetConversation.messages.push(errorBotMessage);
                targetConversation.updatedAt = new Date().toISOString();
            },

            async sendDecisionMessage(proposal: SimulationProposalType): Promise<void> {
                if (!this.currentConversation) {
                    return;
                }

                const messages = this.currentConversation.messages;
                const sourceMessage =
                    [...messages]
                        .reverse()
                        .find(
                            m =>
                                m.sender === 'bot' &&
                                m.status === 'completed' &&
                                m.simulationProposals?.some(p => p.id === proposal.id),
                        ) ?? [...messages].reverse().find(m => m.sender === 'bot' && m.status === 'completed');

                const agentRecommendation = sourceMessage?.text ?? '';

                const userMessage: ChatbotMessageType = {
                    id: uuidv4(),
                    text: proposal.title,
                    sender: 'user',
                    timestamp: new Date().toISOString(),
                    type: 'text',
                    status: 'completed',
                };

                this.addMessage(userMessage);

                const botMessageId = uuidv4();
                const botMessage: ChatbotMessageType = {
                    id: botMessageId,
                    text: '',
                    sender: 'bot',
                    timestamp: new Date().toISOString(),
                    type: 'text',
                    status: 'streaming',
                    isStreaming: true,
                    streamingBuffer: '',
                    toolCalls: [],
                    reasoning: '',
                    isReasoningCollapsed: undefined,
                    hasReceivedMessage: false,
                    hasReceivedReasoning: false,
                };

                this.addMessage(botMessage);

                const { streamDecision } = useStreamingChat();
                const chatId = this.currentConversation.id;

                try {
                    await streamDecision(agentRecommendation, proposal, Number(chatId), {
                        onTextUpdate: text => this.updateProgressiveText(botMessageId, text),
                        onReasoningUpdate: reasoning => this.updateReasoningText(botMessageId, reasoning),
                        onToolCall: toolCall => this.addToolCallToMessage(botMessageId, toolCall),
                        onMetadata: metadata => this.updateMessageMetadata(botMessageId, metadata),
                        onComplete: buffer => this.finalizeStreamingMessage(botMessageId, buffer),
                        onError: error => {
                            console.error('Decision streaming error:', error);
                            this.handleStreamError(botMessageId, error);
                        },
                    });
                } catch (error) {
                    console.error('Error in decision streaming:', error);
                    this.addErrorMessageToChat(error, botMessageId);
                }
            },

            setCurrentConversation(conversationId: number | string): void {
                const conv = this.conversations.find(c => c.id === conversationId);
                if (conv) {
                    this.currentConversation = conv;
                    this.isDraftMode = false;
                }
            },

            async initializeStore(): Promise<void> {
                this.isLoadingConversations = true;
                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint: '/ai-chats',
                    onSuccess: (response: { data: AiChatListResponseType }) => {
                        this.conversations = response.data.items.map(mapApiChatToConversation);
                    },
                    canShowSnackbar: false,
                });
                this.isLoadingConversations = false;
            },
            toggleSidebarExpanded(): void {
                this.sidebarExpanded = !this.sidebarExpanded;
            },
            setSidebarExpanded(expanded: boolean): void {
                this.sidebarExpanded = expanded;
            },
        },
    },
);
